import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clearSupabaseStorage } from '../utils/supabaseClient';
import { 
  FaImages, 
  FaUpload, 
  FaSignInAlt, 
  FaSignOutAlt, 
  FaUserShield 
} from 'react-icons/fa';
import { 
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Avatar,
  Tooltip,
  Slide,
  Fade,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/adminUtils';

const Header = () => {
  const { user, signOut, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Verificar si estamos en una página de perfil
  const isProfilePage = location.pathname.includes('/profile/');

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Verificar si el usuario es administrador
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const adminStatus = await isAdmin(user);
          setIsUserAdmin(adminStatus);
        } catch (error) {
          setIsUserAdmin(false);
        }
      } else {
        setIsUserAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user]);

  const handleSignOut = async () => {
    try {
      // Cerrar el menú de usuario primero
      handleUserMenuClose();
      
      // Limpiar el almacenamiento antes de cerrar sesión
      clearSupabaseStorage();
      sessionStorage.clear();
      
      // Realizar el cierre de sesión
      await signOut();
      
      // Esperar un momento para asegurarnos de que todo se ha limpiado
      setTimeout(() => {
        // Redirigir explícitamente a la página de login usando replace para evitar volver atrás
        window.location.replace('/login');
      }, 100);
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Incluso si hay error, limpiar el almacenamiento
      clearSupabaseStorage();
      sessionStorage.clear();
      
      // En caso de error, también redirigir
      window.location.replace('/login');
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleClose();
    handleUserMenuClose();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getInitials = (email) => {
    if (!email) return 'U';
    return email.charAt(0).toUpperCase();
  };

  return (
    <Slide appear={false} direction="down" in={!scrolled}>
      <AppBar 
        position="fixed" 
        elevation={scrolled ? 4 : 0}
        sx={{ 
          backgroundColor: scrolled
            ? 'rgba(18, 18, 18, 0.92)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'background-color 200ms ease',
          boxShadow: 'none',
          borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.10)' : '1px solid transparent'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <Fade in={true} timeout={1000}>
              <Box 
                component={Link} 
                to="/gallery" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <FaImages
                  size={22}
                  color="#bb86fc"
                  style={{ marginRight: '10px' }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    // La marca conserva su color; #4f3cc9 sobre #121212 daba
                    // 2.24:1 y era ilegible. El morado del producto da 7:1.
                    color: '#bb86fc',
                    fontWeight: 500,
                    letterSpacing: 0,
                    fontSize: { xs: '1.0625rem', md: '1.125rem' }
                  }}
                >
                  {isExtraSmall ? 'GFG' : 'Gallery for Gaymers'}
                </Typography>
              </Box>
            </Fade>

            {/* Desktop Navigation */}
            {!useMediaQuery('down', 'md') ? (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                
                {user && isProfilePage && (
                  <Button
                    component={Link}
                    to={`/upload?profile=${location.pathname.split('/profile/')[1]}`}
                    color="inherit"
                    aria-label="Upload"
                    startIcon={<FaUpload />}
                    sx={{
                      // En pantalla angosta queda solo el icono: con el texto
                      // medía 104px y caía suelto en el medio de la barra, sin
                      // agruparse ni con la marca ni con el avatar.
                      minWidth: 0,
                      px: { xs: 1, sm: 1.5 },
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.56)',
                      '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: 0 },
                      '&:hover': {
                        color: 'rgba(255, 255, 255, 0.92)',
                        backgroundColor: 'rgba(255, 255, 255, 0.09)'
                      }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Upload
                    </Box>
                  </Button>
                )}
                
                {/* Las opciones de Admin ahora están en el menú de perfil */}
                
                {user ? (
                  // El hueco hacia el boton de subir se achica: son dos
                  // controles del mismo grupo y tienen que leerse juntos.
                  <Box sx={{ ml: 0.5 }}>
                      <Tooltip title={user.email || 'User'}>
                        <IconButton
                          onClick={handleUserMenuOpen}
                          size="small"
                          sx={{
                            ml: 2,
                            padding: 0,
                            transition: 'background-color 160ms ease'
                          }}
                        >
                          {/* El avatar es chrome, no la marca: el unico color
                              del encabezado tiene que ser el logo. */}
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.8125rem',
                              bgcolor: 'rgba(255, 255, 255, 0.09)',
                              color: 'rgba(255, 255, 255, 0.92)',
                              fontWeight: 500,
                              border: '1px solid rgba(255, 255, 255, 0.10)'
                            }}
                          >
                            {getInitials(user.email)}
                          </Avatar>
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={userMenuAnchor}
                        open={Boolean(userMenuAnchor)}
                        onClose={handleUserMenuClose}
                        PaperProps={{
                          elevation: 3,
                          sx: {
                            mt: 1.5,
                            minWidth: 180,
                            borderRadius: 2,
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
                            '&:before': {
                              content: '""',
                              display: 'block',
                              position: 'absolute',
                              top: 0,
                              right: 14,
                              width: 10,
                              height: 10,
                              bgcolor: 'background.paper',
                              transform: 'translateY(-50%) rotate(45deg)',
                              zIndex: 0,
                            },
                          },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      >
                        <MenuItem disabled sx={{ opacity: 0.7 }}>
                          <Typography variant="body2">
                            {user.email}
                          </Typography>
                        </MenuItem>
                        <Divider />
                        {isUserAdmin && (
                          <MenuItem onClick={() => handleMenuItemClick('/admin')}>
                            <FaUserShield style={{ marginRight: '8px' }} />
                            Admin Panel
                          </MenuItem>
                        )}
                        {isProfilePage && (
                          <MenuItem onClick={() => handleMenuItemClick(`/upload?profile=${location.pathname.split('/profile/')[1]}`)}>
                            <FaUpload style={{ marginRight: '8px' }} />
                            Upload Images
                          </MenuItem>
                        )}
                        {isUserAdmin && (
                          <MenuItem onClick={() => handleMenuItemClick('/admin/tools')}>
                            <FaUserShield style={{ marginRight: '8px' }} />
                            Admin Tools
                          </MenuItem>
                        )}
                        <Divider />
                        <MenuItem onClick={handleSignOut}>
                          <FaSignOutAlt style={{ marginRight: '8px' }} />
                          Sign Out
                        </MenuItem>
                      </Menu>
                    </Box>
                ) : (
                  <Fade in={true} timeout={2000}>
                    <Button
                      component={Link}
                      to="/login"
                      variant="contained"
                      color="primary"
                      startIcon={<FaSignInAlt />}
                      sx={{ 
                        ml: 2,
                        px: 2,
                        py: 1,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        fontWeight: 500,
                        transition: 'background-color 160ms ease',
                        '&:hover': { boxShadow: 'none' }
                      }}
                    >
                      Sign In
                    </Button>
                  </Fade>
                )}
              </Box>
            ) : (
              // Mobile Navigation
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMenu}
                  sx={{ 
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1.5,
                      borderRadius: 2,
                      minWidth: 180,
                      maxWidth: '90vw',
                      '& .MuiMenuItem-root': {
                        py: 1.5,  // Más espacio vertical para facilitar el toque
                        fontSize: '1rem'
                      }
                    }
                  }}
                >
                  {/* La opción Gallery se ha eliminado ya que el logo GFG cumple la misma función */}
                  
                  {user && isProfilePage && (
                    <MenuItem 
                      onClick={() => handleMenuItemClick(`/upload?profile=${location.pathname.split('/profile/')[1]}`)}
                      selected={isActive('/upload')}
                    >
                      <FaUpload style={{ marginRight: '8px' }} />
                      Upload Images
                    </MenuItem>
                  )}
                  
                  {/* Las opciones de Admin ahora están en el menú de perfil */}
                  
                  {user && (
                    <>
                      <Divider />
                      {isUserAdmin && (
                        <MenuItem onClick={() => handleMenuItemClick('/admin')}>
                          <FaUserShield style={{ marginRight: '8px' }} />
                          Admin Panel
                        </MenuItem>
                      )}
                      {isUserAdmin && (
                        <MenuItem onClick={() => handleMenuItemClick('/admin/tools')}>
                          <FaUserShield style={{ marginRight: '8px' }} />
                          Admin Tools
                        </MenuItem>
                      )}
                      <Divider />
                      <MenuItem onClick={handleSignOut}>
                        <FaSignOutAlt style={{ marginRight: '8px' }} />
                        Sign Out
                      </MenuItem>
                    </>
                  )}
                  
                  {!user && (
                    <MenuItem onClick={() => handleMenuItemClick('/login')}>
                      <FaSignInAlt style={{ marginRight: '8px' }} />
                      Sign In
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    </Slide>
  );
};

export default Header;
